import { groupBy } from "lodash-es";
import { v7 } from "uuid";

import type { Contact, ContactEvent } from "@/features/contact/types";
import {
  EventByTypeGroup,
  EventsByDayGroup,
} from "@/features/inbox/components/conversation/conversation-body/body/group";
import {
  ACTIVITY_TYPE,
  type Activity,
  type ActivityType,
} from "@/types/activity";
import {
  Attachment,
  Message,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  MessageStatus,
  MessageStatusRecord,
  OWNER,
  SEND_EVENT_MESSAGE_TYPE,
  SendCommentPayload,
  SendMessagePayload,
} from "@/types/message";
import { User } from "@/types/user";

const INBOX_VISIBLE_ACTIVITY_TYPES = new Set<ActivityType>([
  ACTIVITY_TYPE.CREATED,
  ACTIVITY_TYPE.NOTE,
  ACTIVITY_TYPE.ASSIGNED,
  ACTIVITY_TYPE.UNASSIGNED,
  ACTIVITY_TYPE.CONVERSATION_STATUS_CHANGE,
  ACTIVITY_TYPE.LIFECYCLE_CHANGE,
  ACTIVITY_TYPE.APPOINTMENT_CREATED,
  ACTIVITY_TYPE.APPOINTMENT_UPDATED,
  ACTIVITY_TYPE.APPOINTMENT_RESCHEDULED,
  ACTIVITY_TYPE.APPOINTMENT_DELETED,
  ACTIVITY_TYPE.ORDER_UPDATE,
  ACTIVITY_TYPE.CALL,
  ACTIVITY_TYPE.BLOCK,
  ACTIVITY_TYPE.UNBLOCK,
  ACTIVITY_TYPE.ADS_CLICKED,
  ACTIVITY_TYPE.SNOOZE,
  ACTIVITY_TYPE.UNSNOOZE,
  ACTIVITY_TYPE.TAG_ADDED,
  ACTIVITY_TYPE.TAG_REMOVED,
  ACTIVITY_TYPE.WEBSITE_VISITED,
  ACTIVITY_TYPE.FORM_SUBMITTED,
]);

export const makeEventFromSendMessage = (
  message: SendMessagePayload,
  user: User,
  ConversationLinkedMessage?: Message | null,
): Message => {
  let attachments: Attachment[] = [];
  const isQuickReply = message.type === SEND_EVENT_MESSAGE_TYPE.QUICK_REPLY;

  if (
    message.type !== SEND_EVENT_MESSAGE_TYPE.TEXT &&
    message.type !== SEND_EVENT_MESSAGE_TYPE.QUICK_REPLY
  ) {
    attachments.push({
      ...(message[message.type] as any),
      type: message.type,
      updatedAt: new Date(),
      createdAt: new Date(),
      externalId: "",
    });
  }

  return {
    attachments,
    type: isQuickReply
      ? MESSAGE_TYPE.QUICK_REPLY
      : attachments.length > 0
        ? MESSAGE_TYPE.ATTACHMENT
        : MESSAGE_TYPE.TEXT,
    ...(isQuickReply && { payload: message.quickReply }),
    isAiAssisted: Boolean(message.isAiAssisted),
    text: message.text!,
    createdAt: new Date().toISOString(),
    externalId: "",
    id: v7(),
    organizationId: user.organizationId,
    channel: { id: message.channelId },
    owner: OWNER.USER,
    reactions: [],
    linked: ConversationLinkedMessage ?? null,
    statuses: [
      {
        messageId: v7(),
        id: v7(),
        createdAt: new Date(),
        receivedAt: null,
        status: MESSAGE_STATUS.SENDING,
        updatedAt: new Date(),
      },
    ],
    updatedAt: new Date().toISOString(),
    user: user,
    error: null,
    receivedAt: null,
  };
};

export const makeEventFromSendComment = (
  message: SendCommentPayload,
  user: User,
  ConversationLinkedMessage?: Message | null,
): Message => {
  let attachments: Attachment[] = [];

  return {
    attachments,
    type: MESSAGE_TYPE.COMMENT,
    text: message.text!,
    createdAt: new Date().toISOString(),
    externalId: "",
    id: v7(),
    organizationId: user.organizationId,
    channel: { id: message.channelId },
    owner: OWNER.USER,
    reactions: [],
    linked: ConversationLinkedMessage ?? null,
    statuses: [
      {
        messageId: v7(),
        id: v7(),
        createdAt: new Date(),
        receivedAt: null,
        status: MESSAGE_STATUS.SENDING,
        updatedAt: new Date(),
      },
    ],
    updatedAt: new Date().toISOString(),
    user: user,
    error: null,
    receivedAt: null,
  };
};

export const hasEventPayload = (event: ContactEvent): boolean =>
  event.type === "message" ? !!event.message : !!event.activity;

export function groupEventsByDayAndOwner(
  events: ContactEvent[],
  contact: Contact,
): EventsByDayGroup[] {
  const _events = events
    .filter(
      (event) =>
        hasEventPayload(event) &&
        (event.type !== "activity" ||
          INBOX_VISIBLE_ACTIVITY_TYPES.has(event.activity.type)),
    )
    .sort(
      (eventA, eventB) =>
        new Date(eventA.createdAt).getTime() -
        new Date(eventB.createdAt).getTime(),
    );
  const groupedByDay = groupBy(_events, (e) => {
    try {
      return new Date(e.createdAt)?.toISOString().split("T")[0];
    } catch (error) {
      console.log("event with error", e, e.createdAt);
      throw error;
    }
  });

  const result: EventsByDayGroup[] = Object.entries(groupedByDay)
    .sort(
      ([dateA], [dateB]) =>
        new Date(dateA).getTime() - new Date(dateB).getTime(),
    )
    .map(([date, dailyEvents]) => {
      const eventGroups: EventByTypeGroup[] = [];
      let currentMessageGroup: (EventByTypeGroup & { type: "message" }) | null =
        null;
      let currentActivityGroup:
        | (EventByTypeGroup & { type: "activity" })
        | null = null;

      for (const event of dailyEvents) {
        if (event.type === "activity") {
          currentMessageGroup = null;
          if (currentActivityGroup) {
            currentActivityGroup.activities.push(event.activity);
          } else {
            currentActivityGroup = {
              type: "activity",
              activities: [event.activity],
            };
            eventGroups.push(currentActivityGroup);
          }
        } else if (event.type === "message") {
          currentActivityGroup = null;
          const message = event.message;

          const isAgent = message.owner === OWNER.USER;
          const isUser = message.owner === OWNER.CONTACT;
          // Show both the human + AI avatars whenever a human agent is attached
          // to an AI-touched message: either explicitly flagged AI-assisted
          // (composer draft/rewrite, approved reply) OR owner is `ai` but a human
          // actually sent it (user_id captured). `owner` is the attribution;
          // user_id is who really sent it.
          const isAiAssisted =
            Boolean(message.user) &&
            (message.owner === OWNER.AI || Boolean(message.isAiAssisted));
          // Assisted messages keep the human agent on the group so its avatar
          // renders alongside the AI chip.
          const carriesUser = isAgent || isAiAssisted;
          const sameOwner =
            currentMessageGroup &&
            currentMessageGroup.owner === message.owner &&
            Boolean(currentMessageGroup.isAiAssisted) === isAiAssisted &&
            ((carriesUser && currentMessageGroup.user?.id === message.user?.id) ||
              isUser);
          const sameChannel =
            currentMessageGroup &&
            currentMessageGroup.channel?.id === message.channel?.id;
          const sameGroup = sameOwner && sameChannel;

          if (sameGroup && currentMessageGroup) {
            currentMessageGroup.messages.push(message);
          } else {
            currentMessageGroup = {
              type: "message",
              messages: [message],
              contact: isUser ? contact : null,
              user: carriesUser ? message.user : null,
              owner: message.owner,
              isAiAssisted,
              channel: { id: message.channel.id },
            };
            eventGroups.push(currentMessageGroup);
          }
        }
      }

      return {
        date,
        eventGroups,
      };
    });

  return result;
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const sortEvents = (events: Array<ContactEvent>) => {
  return events.sort(
    (eventA, eventB) =>
      new Date(eventA.createdAt).getTime() -
      new Date(eventB.createdAt).getTime(),
  );
};

const STATUS_PRIORITY: Record<MessageStatus, number> = {
  [MESSAGE_STATUS.FAILED]: 7,
  [MESSAGE_STATUS.READ]: 6,
  [MESSAGE_STATUS.DELIVERED]: 5,
  [MESSAGE_STATUS.SENT]: 4,
  [MESSAGE_STATUS.PENDING]: 3,
  [MESSAGE_STATUS.RECEIVED]: 2,
  [MESSAGE_STATUS.SENDING]: 1,
};

export const findActivityInEvents = (
  events: ContactEvent[] | undefined,
  activityId: string,
): Activity | undefined => {
  if (!events) return undefined;
  for (const event of events) {
    if (event.type === "activity" && event.activity.id === activityId) {
      return event.activity;
    }
  }
  return undefined;
};

export const replaceActivityInEvents = (
  events: ContactEvent[] | undefined,
  activity: Activity,
): ContactEvent[] | undefined => {
  if (!events) return events;
  return events.map((event) => {
    if (event.type === "activity" && event.activity.id === activity.id) {
      return { ...event, activity };
    }
    return event;
  });
};

export const sortStatus = (statuses: Array<MessageStatusRecord>) => {
  return [...statuses].sort((statusA, statusB) => {
    const priorityA = STATUS_PRIORITY[statusA.status] ?? 0;
    const priorityB = STATUS_PRIORITY[statusB.status] ?? 0;

    return priorityB - priorityA;
  });
};
